import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthShell } from '../components/AuthShell';
import { GoogleButton } from '../components/GoogleButton';
import { Field, ErrorBanner } from '../components/ui';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (params.get('error') === 'google') {
      setError('Google sign-in failed. Please try again.');
    }
  }, [params]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email, password);
      navigate(next ? decodeURIComponent(next) : '/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unable to sign in. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const registerHref = next ? `/register?next=${encodeURIComponent(next)}` : '/register';

  return (
    <AuthShell
      title="Welcome back"
      subtitle={next ? 'Sign in to make your picks for this week.' : 'Sign in to make your picks.'}
    >
      {error && (
        <div className="mb-4">
          <ErrorBanner message={error} />
        </div>
      )}

      {/* 1. Google first */}
      <GoogleButton next={next} label="Continue with Google" divider="below" />

      {/* 2. Email + password */}
      <form onSubmit={onSubmit} noValidate>
        <Field label="Email" htmlFor="email">
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Password" htmlFor="password">
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <button type="submit" className="btn-primary btn-lg w-full" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      {/* 3. Prominent create-account CTA */}
      <div className="mt-6 border-t border-line pt-5 text-center">
        <p className="mb-2 text-sm text-muted">New to Lost Grove Pick&nbsp;’Em?</p>
        <Link to={registerHref} className="btn-outline btn-lg w-full">
          Create an account
        </Link>
      </div>

      <p className="mt-4 text-center text-sm">
        <Link to="/forgot-password" className="text-muted underline">
          Forgot your password?
        </Link>
      </p>
    </AuthShell>
  );
}
